import {
  Form,
  Row,
  Col,
  Input,
  Select,
  Button,
  Typography,
  message,
  Space,
  Switch,
} from "antd";
import classes from "./CreateScenario.module.scss";
import React, { useEffect } from "react";
import TextArea from "antd/es/input/TextArea";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createScenario,
  fetchIndividualScenarios,
  updateScenario,
} from "../../services/scenarios";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { createObjectiveStatuses, queryKeys } from "../../constants";

const { Title, Text } = Typography;

const useParamsQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const CreateScenario = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const query = useParamsQuery();
  const id = query.get("id");

  const [advancedOptions, setAdvancedOptions] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const onChange = (checked) => {
    setAdvancedOptions(checked);
  };

  const getIndividualScenarioQuery = useQuery({
    queryKey: [queryKeys.individualScenario, id],
    queryFn: () => fetchIndividualScenarios(id),
    enabled: !!id,
  });

  const createScenarioMutation = useMutation({
    mutationFn: createScenario,
    onSuccess: () => {
      message.success("Scenario created successfully");
      setLoading(false);
      navigate("/home");
    },
    onError: (error) => {
      message.error(`Error: ${error.message}`);
      setLoading(false);
    },
  });

  const updateScenarioMutation = useMutation({
    mutationFn: updateScenario,
    onSuccess: () => {
      message.success("Scenario updated successfully");
      setLoading(false);
      navigate("/home");
    },
    onError: (error) => {
      message.error(`Error: ${error.message}`);
      setLoading(false);
    },
  });

  const onFinish = (values) => {
    // Handle form submission by calling the mutation
    setLoading(true);
    if (id) {
      updateScenarioMutation.mutate({
        id,
        updateData: {
          ...values,
        },
      });
      return;
    }
    createScenarioMutation.mutate({
      ...values,
    });
  };

  useEffect(() => {
    if (getIndividualScenarioQuery?.data?.data) {
      const { data } = getIndividualScenarioQuery.data;
      form.setFieldsValue({ ...data, timeline: dayjs(data.timeline) } ?? {});
    }
  }, [getIndividualScenarioQuery?.data, form]);

  return (
    <>
      <Space direction="vertical" style={{ margin: "10px 0px" }}>
        <Title level={3} style={{ margin: 0 }}>
          {id ? "Edit Objective" : "Create New Objective"}
        </Title>
        <Text type="secondary">
          {id
            ? "Please update the fields to edit the Objective."
            : " Please complete the fields to create a new Objective."}
        </Text>
      </Space>

      {/* <Space
        style={{
          marginBottom: "20px",
        }}
      > */}
        <Form
          form={form}
          layout="vertical"
          name="create-objective"
          requiredMark="optional"
          onFinish={onFinish}
        >
          <Row gutter={[8, 0]}>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label="Title"
                name="title"
                rules={[
                  {
                    required: true,
                    message: "Please input the scenario title!",
                  },
                ]}
              >
                <Input placeholder="Scenario Title" />
              </Form.Item>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label="Status"
                name="status"
                rules={[
                  {
                    required: true,
                    message: "Please select your scenario status",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a scenario status"
                  options={createObjectiveStatuses}
                />
              </Form.Item>
            </Col>
            <Col md={24} sm={24} xs={24}>
              <Form.Item
                label="Objectives"
                name="objectives"
                rules={[
                  { required: true, message: "Please type your objectives" },
                ]}
              >
                <TextArea rows={2} placeholder="Objectives" />
              </Form.Item>
            </Col>
            <Col md={8} sm={24} xs={24}>
              <Form.Item
                label="Objective Type"
                name="scenario_type"
                rules={[
                  {
                    required: true,
                    message: "Please select your scenario type",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a scenario type"
                  options={[
                    { value: "ENVIRONMENTAL", label: "Environmental" },
                    { value: "BUSINESS", label: "Business" },
                    { value: "ECONOMIC", label: "Economic" },
                    { value: "TECHNOLOGICAL", label: "Technological" },
                    { value: "POLITICAL", label: "Political" },
                    { value: "LEGAL", label: "Legal" },
                    { value: "HEALTH", label: "Health" },
                    { value: "CULTURAL", label: "Cultural" },
                    { value: "FINANCIAL", label: "Financial" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col md={8} sm={24} xs={24}>
              <Form.Item
                label="Priority Level"
                name="priority_level"
                rules={[
                  {
                    required: true,
                    message: "Please select your priority level",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a priority level"
                  options={[
                    { value: "LOW", label: "Low" },
                    { value: "MEDIUM", label: "Medium" },
                    { value: "HIGH", label: "High" },
                    { value: "CRITICAL", label: "Critical" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col md={8} sm={24} xs={24}>
              <Form.Item label="Budget Estimate" name="budget_estimate">
                <Input placeholder="Budget Estimate" type="number" />
              </Form.Item>
            </Col>
          </Row>
          <div className={classes.advancedContainer}>
            <div className={classes.advancedHeader}>
              <Title level={3} style={{ marginBottom: 20, marginTop: 0 }}>
                Advanced Objective Analysis
              </Title>
              {/*<Switch value={advancedOptions} onChange={onChange} />*/}
              <Switch
                checked={advancedOptions}
                onChange={(checked) => {
                  onChange(checked); // this should update `advancedOptions` state
                  form.setFieldsValue({ has_advanced_objectives: checked });
                }}
              />
            </div>
            {advancedOptions && (
              <Row gutter={[8, 0]}>
                <Col md={12} sm={24} xs={24}>
                  <Form.Item label="Plan" name="plan">
                    <TextArea rows={2} placeholder="Plan" />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24} xs={24}>
                  <Form.Item label="Dependencies" name="dependencies">
                    <TextArea rows={2} placeholder="Dependencies" />
                  </Form.Item>
                </Col>
                <Form.Item name="has_advanced_objectives" hidden>
                  <Input type="hidden" />
                </Form.Item>
              </Row>
            )}
          </div>

          <Form.Item style={{ marginBottom: "0px", marginTop: 10 }}>
            <div className={classes.buttonContainer}>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => navigate("/home")}
              >
                Back
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {id ? "Update" : "Submit"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      {/* </Space> */}
    </>
  );
};

export default CreateScenario;
